import React from "react";
import { api } from "@/lib/api";
import { Truck, CheckCircle2, CircleDot, AlertTriangle, Lock } from "lucide-react";
import { formatDateTime } from "@/lib/datetime";

const LOAD_STATUS = { loading: "Loading", confirmed: "Loaded", departed: "Departed" };
const ICON = {
  loaded: <CheckCircle2 className="w-4 h-4 text-emerald-500" />,
  partial: <CircleDot className="w-4 h-4 text-amber-500" />,
  pending: <CircleDot className="w-4 h-4 text-slate-300" />,
  missing: <AlertTriangle className="w-4 h-4 text-rose-500" />,
};
const n = (v) => Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

function ItemRow({ it }) {
  const key = `${it.assignment_id || it.pick_list_id}-${it.line_id}`;
  return (
    <li className="flex items-start justify-between gap-3 py-2.5" data-testid={`loaded-item-${key}`}>
      <div className="min-w-0">
        <div className="text-sm font-medium text-slate-900">{it.title || it.sku}</div>
        <div className="text-[11px] text-slate-500">{[it.sku, it.barcode].filter(Boolean).join(" · ")}</div>
        {it.serial_numbers.length > 0 && (
          <div className="mt-0.5 text-xs text-slate-700" data-testid={`loaded-item-serials-${key}`}>Serial: {it.serial_numbers.join(", ")}</div>
        )}
        {it.missing_reason && <div className="mt-0.5 text-xs text-rose-600">Missing: {it.missing_reason}</div>}
        {it.loaded_at && <div className="text-[11px] text-slate-400">Loaded {formatDateTime(it.loaded_at)}{it.loaded_by_name ? ` by ${it.loaded_by_name}` : ""}</div>}
      </div>
      <div className="flex shrink-0 items-center gap-1.5 text-sm font-semibold tabular-nums text-slate-900" data-testid={`loaded-item-qty-${key}`}>
        {n(it.qty_loaded)}/{n(it.qty_required)} {ICON[it.line_status]}
      </div>
    </li>
  );
}

export function JobLoadedItems({ legacyId }) {
  const [data, setData] = React.useState(null);
  React.useEffect(() => {
    api.get(`/tasks/${legacyId}/loaded-items`).then((r) => setData(r.data)).catch(() => setData({ items: [], loads: [] }));
  }, [legacyId]);
  if (!data) return null;
  const vanGroups = Object.values(data.items.filter((i) => !i.assignment_id).reduce((acc, it) => {
    const k = it.vehicle_id || "none";
    (acc[k] = acc[k] || { key: k, label: it.vehicle_registration || (it.vehicle_id ? `Van ${it.vehicle_id}` : "Van"), items: [] }).items.push(it);
    return acc;
  }, {}));
  const empty = data.loads.length === 0 && vanGroups.length === 0;
  return (
    <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="task-loaded-items">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center"><Truck className="w-4 h-4" /></span>
          <div>
            <h2 className="text-base font-bold text-slate-900">Products loaded</h2>
            <p className="text-[11px] text-slate-500">Live from the van load for this job</p>
          </div>
        </div>
        {!data.can_edit_stock && <span className="inline-flex items-center gap-1 text-[11px] text-slate-400" data-testid="loaded-items-readonly"><Lock className="w-3 h-3" />Read-only</span>}
      </div>
      {empty && (
        <p className="text-sm text-slate-500" data-testid="loaded-items-empty">Nothing loaded onto a van for this job yet.</p>
      )}
      {vanGroups.map((g) => (
        <div key={g.key} className="mt-2" data-testid={`loaded-van-${g.key}`}>
          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
            <span className="font-semibold text-slate-800">{g.label}</span>
            <span className="rounded-full bg-emerald-50 text-emerald-700 px-2 py-0.5 font-medium">Loaded</span>
          </div>
          <ul className="divide-y divide-slate-100">
            {g.items.map((it) => <ItemRow key={`${it.pick_list_id}-${it.line_id}`} it={it} />)}
          </ul>
        </div>
      ))}
      {data.loads.map((l) => (
        <div key={l.assignment_id} className="mt-2" data-testid={`loaded-load-${l.assignment_id}`}>
          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
            <span className="font-semibold text-slate-800">{l.vehicle_registration || l.vehicle_id}</span>
            <span>{l.run_date}</span>
            <span className="rounded-full bg-slate-100 px-2 py-0.5 font-medium" data-testid={`loaded-load-status-${l.assignment_id}`}>{LOAD_STATUS[l.status] || l.status}</span>
          </div>
          <ul className="divide-y divide-slate-100">
            {data.items.filter((i) => i.assignment_id === l.assignment_id).map((it) => <ItemRow key={it.line_id} it={it} />)}
          </ul>
        </div>
      ))}
    </section>
  );
}
